import React, { useEffect } from 'react';
import {
  Box,
  IconButton,
  Typography,
  useTheme,
} from '@material-ui/core';

import {
  Attachment as AttachmentIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';

import ReactResizeDetector from 'react-resize-detector';
import {
  Column,
  Table,
  TableHeaderRowProps,
  TableRowProps,
} from 'react-virtualized';
import classNames from 'classnames';
import { encode } from 'hi-base64';
import PopupDialog from '../../../../components/popupDialog';

import {
  AttachInfo,
  RequestAttachListArgs,
  ResultDetailsDispatchFunc,
  ResultDetailsInfo,
} from '../types';

import styles from './AttachListPopup.styles';

type Props = {
  resultDetails?: ResultDetailsInfo;
  attachListPopup: {
    isOpen: boolean;
    resultDetailId?: string;
  };
  attachList?: AttachInfo[];
  requestAttachList: ResultDetailsDispatchFunc<RequestAttachListArgs>;
  closeAttachListPopup: ResultDetailsDispatchFunc<unknown>;
};

const useStyles = makeStyles(styles);
const AttachListPopup: React.FC<Props> = ({
  resultDetails,
  attachListPopup: {
    isOpen,
    resultDetailId,
  },
  attachList,
  requestAttachList,
  closeAttachListPopup,
}: Props) => {
  const classes = useStyles();
  const theme = useTheme();

  useEffect(() => {
    if (isOpen && !attachList && resultDetailId) {
      requestAttachList({ resultDetailId });
    }
  }, [attachList, isOpen, requestAttachList, resultDetailId]);

  const headerRowRenderer = ({
    columns,
    style,
  }: TableHeaderRowProps) => (
    <Box
      className={classes.tableHeaderRow}
      role="row"
      style={style}
    >
      {columns}
    </Box>
  );

  const rowRenderer = ({
    key,
    columns,
    style,
    index,
  }: TableRowProps) => (
    <Box
      key={key}
      className={
        classNames(
          index % 2 === 0 ? classes.tableEvenRow : classes.tableOddRow,
          classes.tableHover,
        )
      }
      role="row"
      style={style}
    >
      {columns}
    </Box>
  );

  return (
    <PopupDialog
      open={isOpen}
      title="Attach List"
      closeAction={closeAttachListPopup}
      maxWidth="md"
      classes={{
        content: classes.root,
      }}
    >
      <Box className={classes.details}>
        <ReactResizeDetector handleHeight handleWidth>
          {({ width, height }) => (
            <Table
              height={(height || 0) - 8 || 0}
              width={width || 0}
              rowCount={attachList?.length || 0}
              rowGetter={({ index }) => (attachList || [])[index]}
              rowHeight={theme.spacing(4)}
              headerHeight={theme.spacing(4)}
              headerRowRenderer={headerRowRenderer}
              headerClassName={classNames(
                classes.tableHeaderCell,
              )}
              gridClassName={classes.tableGrid}
              rowRenderer={rowRenderer}
              className={classes.table}
            >
              <Column
                label="File name"
                cellDataGetter={({ rowData }) => (rowData as AttachInfo).name}
                dataKey="name"
                width={600}
                className={classes.tableCell}
              />
              <Column
                label="Type"
                cellDataGetter={({ rowData }) => (rowData as AttachInfo).fileType}
                dataKey="fileType"
                width={200}
                className={classes.tableCell}
                cellRenderer={({ rowData }) => (
                  <Typography
                    component="span"
                    color="inherit"
                    variant="body2"
                    align="center"
                  >
                    {(rowData as AttachInfo).fileType === 'pdf' ? 'PDF' : 'Image'}
                  </Typography>
                )}
              />
              <Column
                label="View"
                cellDataGetter={({ rowData }) => (rowData as AttachInfo).id}
                dataKey="id"
                width={200}
                className={classes.tableCell}
                cellRenderer={({ rowData }) => (
                  <IconButton
                    className={classes.linkButton}
                    onClick={() => {
                      const url = [
                        // mode
                        'attach',
                        // requestId
                        `${atob(resultDetails?.requestInfo.id || '').split('|')[2]}`,
                        // attachmentId
                        `${atob((rowData as AttachInfo).id).split('|')[2]}`,
                        // filePath
                        encode((rowData as AttachInfo).filePath),
                        // fileType
                        (rowData as AttachInfo).fileType,
                      ];

                      const hash = `resultPreview?action=${btoa(url.join('&'))}`;

                      window.open(hash);
                    }}
                  >
                    <AttachmentIcon />
                  </IconButton>
                )}
              />
            </Table>
          )}
        </ReactResizeDetector>
      </Box>
    </PopupDialog>
  );
};

export default AttachListPopup;
